import type { CompanyResponse, LicenceResponse, ListResponse } from '@shared/schemas';
import { z } from 'zod';

import api from '@/shared/api/api-client';

import { enrichmentStatsHttp } from '../../_shared/infrastructure/enrichment-stats.http';
import type { EnrichmentCandidate } from '../domain/candidates.domain';
import { type EnrichmentSession, enrichmentSessionSchema } from '../domain/sessions.domain';
import type { CreateSessionInput, NewSessionScope, SessionsGateway } from './sessions.gateway';

const SESSIONS_ENDPOINT = '/enrichment-sessions';
const sessionsSchema = z.array(enrichmentSessionSchema);

/** Builds the `?licence_id=&list_id=&...` query string for the scoped endpoints. */
function scopeQuery(scope: NewSessionScope, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  if (scope.licenceId) params.set('licence_id', scope.licenceId);
  if (scope.listId) params.set('list_id', scope.listId);
  if (extra) for (const [k, v] of Object.entries(extra)) params.set(k, v);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Real implementation over the shared api client. The single place that knows
 * this feature's endpoints; the shared stats read is spread in from `_shared`.
 * Reads validate at the boundary with Zod (parity with the old React Query hooks).
 */
export const httpSessionsGateway: SessionsGateway = {
  fetchSessions: async () => sessionsSchema.parse(await api.get<unknown>(SESSIONS_ENDPOINT)),

  createSession: ({ type, licenceId, listId, targetIds, force, criteria }: CreateSessionInput) =>
    api.post<EnrichmentSession>(SESSIONS_ENDPOINT, {
      type,
      ...(licenceId ? { licence_id: licenceId } : {}),
      ...(listId ? { list_id: listId } : {}),
      target_ids: targetIds,
      // Sent unconditionally: the pipeline row carries the flag from now on, and
      // omitting it would leave a forced run indistinguishable from a normal one.
      force,
      criteria,
    }),

  fetchLicences: () => api.get<LicenceResponse[]>('/licences'),

  fetchProfileTypes: async () => {
    const rows = await api.get<{ name: string }[]>('/profile-types');
    return rows.map((r) => r.name);
  },

  fetchLists: (licenceId: string) =>
    api.get<ListResponse[]>(`/lists?origin=all&licence_id=${licenceId}`),

  fetchCandidates: (scope: NewSessionScope) =>
    api.get<EnrichmentCandidate[]>(`/profiles/enrichment-candidates${scopeQuery(scope)}`),

  fetchCompanies: (scope: NewSessionScope, currentOnly: boolean) => {
    // current_only is only meaningful when the pool is already scoped by licence/list.
    const extra =
      currentOnly && (scope.licenceId || scope.listId) ? { current_only: 'true' } : undefined;
    return api.get<CompanyResponse[]>(`/companies/from-experiences${scopeQuery(scope, extra)}`);
  },

  ...enrichmentStatsHttp,
};
